import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { Halo } from '@/components/ui';

/**
 * What an authenticated but unregistered user sees (spec §6.3: `403 USER_NOT_REGISTERED`).
 *
 * The registration gate has no exemptions, so this replaces the whole app rather than appearing
 * inside it — there is no screen such a user may look at. It says who to ask, because the answer
 * is always "an administrator has to add you", never "try again".
 *
 * Laid out like the Spectrum sign-in: the message on a white half, the brand mark in its halo on
 * the other.
 */
export function NoAccessPage({
  email,
  reason,
  onSignOut,
}: {
  email?: string | null;
  reason?: string | null;
  onSignOut?: () => void;
}) {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexWrap: 'wrap', bgcolor: 'background.default' }}>
      <Box
        sx={{
          flex: '1 1 480px',
          minWidth: 0,
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 3, md: 8 },
          py: 5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box component="img" src="/glossa.svg" alt="" sx={{ width: 48, height: 48 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
            <Typography
              component="span"
              sx={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}
            >
              GrECo
            </Typography>
            <Typography component="span" sx={{ fontSize: 18, fontWeight: 600, color: 'spectrum.mark' }}>
              Glossa
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            maxWidth: 440,
            width: '100%',
            mx: 'auto',
            py: 6,
          }}
        >
          <Typography variant="h1" sx={{ fontSize: 34, color: 'spectrum.mark', mb: 1.5 }}>
            No access to Glossa
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 15, mb: 3 }}>
            You signed in{email ? ` as ${email}` : ''}, but this account is not registered in Glossa.
            An administrator has to add it and grant a role on a project before anything is visible.
          </Typography>
          {reason ? (
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: 13,
                mb: 3,
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'background.default',
              }}
            >
              {reason}
            </Typography>
          ) : null}
          {onSignOut ? (
            <Button variant="outlined" size="large" onClick={onSignOut} sx={{ alignSelf: 'flex-start' }}>
              Sign in with another account
            </Button>
          ) : null}
        </Box>

        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>GrECo International AG</Typography>
      </Box>

      <Box
        aria-hidden="true"
        sx={{
          flex: '1 1 480px',
          minWidth: 0,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 5,
          p: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'relative', width: 240, height: 240 }}>
          <Halo
            size={520}
            dot={20}
            opacity={0.22}
            sx={{ position: 'absolute', left: -140, top: -140 }}
          />
          <Box
            component="img"
            src="/glossa.svg"
            alt=""
            sx={{ position: 'relative', width: 240, height: 240 }}
          />
        </Box>
        <Typography sx={{ fontSize: 22, fontWeight: 600, color: 'spectrum.mark' }}>
          A matter of trust.
        </Typography>
      </Box>
    </Box>
  );
}
